/**
 * sync-v2 registration. Declares this app's CRDT collections and gates
 * the cross-device sync flow behind a Family-tier opt-in. The heavy
 * lifting (Automerge ops, OPFS persistence, R2 push/pull, presence,
 * time-travel) lives in chassis/src/sync-v2/.
 *
 * v0 contract: this file makes the seam declarable. Calling enableSync()
 * is currently a no-op shim until the chassis package is wired into the
 * app build. Settings UI surfaces the consent flow either way.
 */

const APP_SLUG = 'dial' as const;
type AppSlug = typeof APP_SLUG;

export interface SyncCollection {
  readonly name: string;
  readonly displayName: string;
  readonly schemaVersion: number;
}

export const SYNC_COLLECTIONS: readonly SyncCollection[] = Object.freeze([{name:"contacts",displayName:"Contacts",schemaVersion:1},{name:"gifts",displayName:"Gift log",schemaVersion:1},{name:"circles",displayName:"Circles",schemaVersion:1},{name:"voiceNotes",displayName:"Voice notes",schemaVersion:1}]);

export interface SyncOptions {
  readonly endpoint: string;
  readonly handle: string;
  readonly nickname?: string;
}

export const DEFAULT_ENDPOINT = 'https://sync.taxwalaai.com';

export interface SyncState {
  readonly enabled: boolean;
  readonly handle: string | null;
  readonly endpoint: string;
  readonly device: string | null;
  readonly lastSyncedAt: number | null;
  readonly collections: readonly string[];
}

const STORAGE_KEY = `dial:sync-v2:state`;

export function getSyncState(): SyncState {
  if (typeof localStorage === 'undefined') return DEFAULT_STATE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<SyncState>;
    return Object.freeze({ ...DEFAULT_STATE, ...parsed });
  } catch {
    return DEFAULT_STATE;
  }
}

const DEFAULT_STATE: SyncState = Object.freeze({
  enabled: false,
  handle: null,
  endpoint: DEFAULT_ENDPOINT,
  device: null,
  lastSyncedAt: null,
  collections: Object.freeze(SYNC_COLLECTIONS.map(c => c.name)),
});

export async function enableSync(opts: SyncOptions): Promise<{ ok: boolean; reason?: string }> {
  // v0 seam — records consent + intent, doesn't drive engine yet. The
  // chassis sync-v2 modules ship in feat/2030-fork-14 and are imported by
  // the build step once apps adopt @empire-apps/chassis as a dependency.
  if (typeof localStorage === 'undefined') return { ok: false, reason: 'no-storage' };
  const device = (localStorage.getItem('empire-sync-device') as string | null)
    ?? mintDeviceId();
  localStorage.setItem('empire-sync-device', device);
  const next: SyncState = {
    enabled: true,
    handle: opts.handle,
    endpoint: opts.endpoint,
    device,
    lastSyncedAt: null,
    collections: SYNC_COLLECTIONS.map(c => c.name),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  dispatchSyncChange(next);
  return { ok: true };
}

export function disableSync(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...DEFAULT_STATE, device: null }));
  dispatchSyncChange(DEFAULT_STATE);
}

function mintDeviceId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return `dev_${hex}`;
}

function dispatchSyncChange(s: SyncState): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('dial:sync-change', { detail: s }));
}

// DPDP consent helper — apps call this before enabling cross-device sync.
// Returns the consent-card markup; embed it in a Settings sheet.

export function syncConsentMarkup(): string {
  return `
    <article class="sync-consent" role="region" aria-labelledby="sync-h">
      <h3 id="sync-h">Multi-device sync</h3>
      <p>Sign in once with your Empire passkey. Your <b>${APP_SLUG}</b> data
        flows between your devices. Only ciphertext leaves your phone.</p>
      <ul>
        <li>AES-GCM 256 envelope per change</li>
        <li>We cannot read your data; only your devices can</li>
        <li>Endpoint: <code>${DEFAULT_ENDPOINT}</code></li>
        <li>Withdraw any time. Withdrawal wipes the cloud copy.</li>
      </ul>
      <div class="actions">
        <button type="button" data-action="grant-sync">I consent</button>
        <button type="button" data-action="cancel-sync">Not now</button>
      </div>
    </article>
  `;
}

// Time-travel mount — apps drop this element wherever they want the
// History view. The chassis <empire-time-travel> Web Component renders
// itself; this helper hydrates the buckets list for the registered
// collections once the chassis loads.

export function mountTimeTravel(host: HTMLElement): void {
  const el = document.createElement('empire-time-travel');
  el.setAttribute('data-app', APP_SLUG);
  el.setAttribute('data-doc', `${APP_SLUG}/${SYNC_COLLECTIONS[0]?.name ?? 'main'}`);
  host.innerHTML = '';
  host.appendChild(el);
}

// Registration receipt — called from main.ts so the boot path knows the
// app has a sync seam. Returns the current state for the UI to render.
export function registerSync(): SyncState {
  const state = getSyncState();
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>)[`__${APP_SLUG}_sync_v2`] = {
      collections: SYNC_COLLECTIONS,
      state,
    };
  }
  return state;
}


// Auto-register at import time so main.ts only needs one import line.
if (typeof window !== 'undefined') {
  registerSync();
}
